import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { WordpressContentService } from '../wordpress-content.service';

type BoardState = 'loading' | 'error' | 'empty' | 'content';

interface ContentResult {
  content: string;
  state: Exclude<BoardState, 'loading'>;
}

const BOARD_LIST_PATH = 'about-us';
const DAP_WORDPRESS_ORIGIN = 'https://disasteraccountability.org';

@Component({
  selector: 'about-us-board-of-directors',
  templateUrl: './board-of-directors.component.html',
  styleUrls: ['./board-of-directors.component.css'],
})
export class BoardOfDirectorsComponent implements OnDestroy, OnInit {
  state: BoardState = 'loading';
  content = '';
  private contentSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wordpressContentService: WordpressContentService
  ) {}

  ngOnInit(): void {
    const effectivePath$ = this.route.queryParamMap.pipe(
      map((queryParamMap) => this.getEffectivePath(queryParamMap)),
      distinctUntilChanged(),
      tap(() => {
        this.content = '';
        this.state = 'loading';
      })
    );

    this.contentSubscription = effectivePath$
      .pipe(
        switchMap((path) =>
          this.wordpressContentService.getContent(path).pipe(
            map((response): ContentResult => {
              if (!response.content || !response.content.trim()) {
                return { content: '', state: 'empty' };
              }

              return { content: this.normalizeWordpressContent(response.content), state: 'content' };
            }),
            catchError((): Observable<ContentResult> => of({ content: '', state: 'error' }))
          )
        )
      )
      .subscribe((result) => {
        this.content = result.content;
        this.state = result.state;
      });
  }

  ngOnDestroy(): void {
    this.contentSubscription?.unsubscribe();
  }

  onWordpressClick(event: MouseEvent): void {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const anchor = event.target.closest('a[href]') as HTMLAnchorElement | null;
    if (
      !anchor ||
      !(event.currentTarget instanceof Element) ||
      !event.currentTarget.contains(anchor) ||
      anchor.hasAttribute('target') ||
      anchor.hasAttribute('download')
    ) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (
      !href ||
      href !== href.trim() ||
      href.startsWith('#') ||
      href.includes('\\') ||
      href.includes('?') ||
      href.includes('#') ||
      /[\u0000-\u001f\u007f]/.test(href) ||
      this.hasDotSegment(href)
    ) {
      return;
    }

    let url: URL;
    try {
      url = new URL(href, DAP_WORDPRESS_ORIGIN + '/');
    } catch (_error) {
      return;
    }

    if (url.origin !== DAP_WORDPRESS_ORIGIN || url.search || url.hash) {
      return;
    }

    const path = this.validateBoardPath(url.pathname);
    if (!path) {
      return;
    }

    event.preventDefault();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pathUrl: path },
      queryParamsHandling: 'merge',
    });
  }

  private getEffectivePath(queryParamMap: ParamMap): string {
    const rawPath = queryParamMap.get('pathUrl');
    if (rawPath === null) {
      return BOARD_LIST_PATH;
    }

    const validatedPath = this.validateBoardPath(rawPath);
    if (!validatedPath) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { pathUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return BOARD_LIST_PATH;
    }

    return validatedPath;
  }

  private normalizeWordpressContent(content: string): string {
    const documentNode = new DOMParser().parseFromString(content, 'text/html');
    const headings = documentNode.querySelectorAll('h1');

    headings.forEach((heading) => {
      const replacement = documentNode.createElement('h2');
      Array.from(heading.attributes).forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
      while (heading.firstChild) {
        replacement.appendChild(heading.firstChild);
      }
      heading.replaceWith(replacement);
    });

    return documentNode.body.innerHTML;
  }

  private validateBoardPath(value: string): string | null {
    if (
      !value ||
      value.length > 2048 ||
      value !== value.trim() ||
      value.startsWith('//') ||
      value.includes('\\') ||
      value.includes('?') ||
      value.includes('#') ||
      /[\u0000-\u001f\u007f]/.test(value) ||
      this.hasDotSegment(value)
    ) {
      return null;
    }

    const match = /^\/?(board\/[a-z0-9]+(?:-[a-z0-9]+)*)\/?$/.exec(value);
    return match ? match[1] : null;
  }

  private hasDotSegment(value: string): boolean {
    let decodedValue = value;
    try {
      for (let decodeCount = 0; decodeCount <= value.length; decodeCount += 1) {
        const nextValue = decodeURIComponent(decodedValue);
        if (nextValue === decodedValue) {
          break;
        }
        decodedValue = nextValue;
      }
    } catch (_error) {
      return true;
    }

    return /(?:^|[\/\\])\.{1,2}(?:[\/\\]|$|[?#])/.test(decodedValue);
  }
}
